export enum PlanType {
    PERSONAL = "PERSONAL",
    PROFESSIONAL = "PROFESSIONAL",
    ENTERPRISE = "ENTERPRISE",
    INTERNAL = "INTERNAL"
}

export enum StatisticsType {
    None = "None",
    Basic = "Basic",
    Advanced = "Advanced",
}

export enum BillingType {
    MONTHLY = "MONTHLY",
    YEARLY = "YEARLY",
    FREE = "FREE"
}

/**
 * Define interface of features in a Subscription Plan
 */
export interface SubscriptionPlan {
    type: PlanType;
    name: string;
    description: string;
    monthPrice: number;
    yearPrice: number;
    teamMembers: number;
    numberOfTours: number;
    numberOfStoriesPerTour: number;
    publishedTourCount: number;
    statisticsType: StatisticsType;
    paidTours: boolean;
    transactionFee: string;
    thirdPartySuppliers: boolean;
    storyHuntWorkshops: boolean;
    emailSupport: boolean;
    sellTours: boolean;
    reviewsData: boolean;
    prioritySupport: boolean;
    contentSparring: boolean;
    apiAccess: boolean;
    invoiceBasedBilling: boolean;
}

/**
 * Basic / free subscription plan
 */
class BasicPlan implements SubscriptionPlan {
    type = PlanType.PERSONAL;
    name = `Basic Plan`;
    description = `For the new storyteller(s) who wants to share their story with the world or simply want to create experimental tours.`;
    monthPrice = 0;
    yearPrice = 0;
    numberOfTours = -1;
    numberOfStoriesPerTour = 10;
    sellTours = false;
    reviewsData = false;
    teamMembers = 5;
    publishedTourCount = 0;
    statisticsType = StatisticsType.None;
    paidTours = false;
    transactionFee = '';
    thirdPartySuppliers = false;
    storyHuntWorkshops = false;
    emailSupport = false;
    prioritySupport = false;
    contentSparring = false;
    apiAccess = false;
    invoiceBasedBilling = false;
}

/**
 * Premium subscription plan
 */
class PremiumPlan implements SubscriptionPlan {
    type = PlanType.PROFESSIONAL;
    name = `Premium Plan`;
    description = `For the advanced storyteller(s) with stories to share, who wants to understand their audience better and make a profit.`;
    monthPrice = 1995;
    yearPrice = 19995;
    numberOfTours = -1;
    numberOfStoriesPerTour = -1;
    sellTours = true;
    reviewsData = true;
    teamMembers = 5;
    publishedTourCount = 3;
    statisticsType = StatisticsType.Basic;
    paidTours = true;
    transactionFee = '20 %';
    thirdPartySuppliers = false;
    storyHuntWorkshops = false;
    emailSupport = true;
    prioritySupport = false;
    contentSparring = false;
    apiAccess = false;
    invoiceBasedBilling = false;
}

/**
 * Premium subscription plan
 */
class EnterprisePlan implements SubscriptionPlan {
    type = PlanType.ENTERPRISE;
    name = `Enterprise Plan`;
    description = `For the big team of advanced storyteller(s) who wants to understand their audiences and create elaborate storydriven experiences.`;
    monthPrice = -1;
    yearPrice = -1;
    numberOfTours = -1;
    numberOfStoriesPerTour = -1;
    sellTours = true;
    reviewsData = true;
    teamMembers = -1;
    publishedTourCount = -1;
    statisticsType = StatisticsType.Advanced;
    paidTours = true;
    transactionFee = 'Contact sales';
    thirdPartySuppliers = true;
    storyHuntWorkshops = true;
    emailSupport = true;
    prioritySupport = true;
    contentSparring = true;
    apiAccess = true;
    invoiceBasedBilling = true;
}

/**
 * Internal 'subscription' plan for original StoryHunt tours
 */
class InternalPlan implements SubscriptionPlan {
    type = PlanType.INTERNAL;
    name = `StoryHunt Original`;
    description = `For StoryHunt employers and employees.`;
    monthPrice = -1;
    yearPrice = -1;
    numberOfTours = -1;
    numberOfStoriesPerTour = 10;
    sellTours = true;
    reviewsData = true;
    teamMembers = -1;
    publishedTourCount = -1;
    statisticsType = StatisticsType.Advanced;
    paidTours = true;
    transactionFee = '';
    thirdPartySuppliers = true;
    storyHuntWorkshops = true;
    emailSupport = true;
    prioritySupport = true;
    contentSparring = true;
    apiAccess = true;
    invoiceBasedBilling = true;
}

export const SubscriptionPlanMap: { [key in PlanType]: SubscriptionPlan } = {
    'PERSONAL': new BasicPlan(),
    'PROFESSIONAL': new PremiumPlan(),
    'ENTERPRISE': new EnterprisePlan(),
    'INTERNAL': new InternalPlan(),
};
